import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { TrendingUp, Wallet, PiggyBank, Percent, CalendarRange, Download } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { api } from "@/lib/api";

const STATUS_LABEL = { neu: "Neu", in_bearbeitung: "In Bearbeitung", abgeschlossen: "Abgeschlossen", storniert: "Storniert" };
const PAYMENT_LABEL = { bezahlt: "Bezahlt", unbezahlt: "Unbezahlt", erstattet: "Erstattet" };

const toISO = (d) => d.toISOString().slice(0, 10);

export default function RevenueStatsView() {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("preset"); // "preset" | "custom"
  const [range, setRange] = useState(12);
  const [customStart, setCustomStart] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return toISO(d); });
  const [customEnd, setCustomEnd] = useState(() => toISO(new Date()));
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback((opts = {}) => {
    if (mode === "custom" && (!customStart || !customEnd)) return Promise.resolve();
    const params = mode === "custom" ? { start_date: customStart, end_date: customEnd } : { months: range };
    if (!opts.silent) setLoading(true);
    return api.get("/stats/profit", { params })
      .then((r) => setData(r.data))
      .catch(() => { if (!opts.silent) toast.error("Gewinn-Statistik konnte nicht geladen werden."); })
      .finally(() => { if (!opts.silent) setLoading(false); });
  }, [mode, range, customStart, customEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Live-Aktualisierung: aktualisiert die Tabelle automatisch, sobald neuer Umsatz entsteht,
  // ohne beim Nutzer einen Ladezustand/Flackern auszulösen (stilles Nachladen im Hintergrund).
  useEffect(() => {
    const silentRefresh = () => fetchData({ silent: true });
    const interval = setInterval(silentRefresh, 20000);
    const onVisibility = () => { if (!document.hidden) silentRefresh(); };
    window.addEventListener("focus", silentRefresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", silentRefresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchData]);

  const exportPdf = async () => {
    const win = window.open("", "_blank");
    setExporting(true);
    const params = mode === "custom" ? { start_date: customStart, end_date: customEnd } : { months: range };
    try {
      const res = await api.get("/stats/profit/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (win) win.location.href = url; else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      if (win) win.close();
      toast.error("PDF konnte nicht erstellt werden.");
    }
    finally { setExporting(false); }
  };

  const ku = data?.kleinunternehmer;

  const cards = useMemo(() => [
    { key: "revenue", label: "Umsatz (bezahlt)", net: data?.total_revenue_net, gross: data?.total_revenue_gross, Icon: TrendingUp },
    { key: "cost", label: "Einkaufskosten", net: data?.total_cost_net, gross: data?.total_cost_gross, Icon: Wallet },
    { key: "profit", label: "Gewinn", net: data?.total_profit_net, gross: data?.total_profit_gross, Icon: PiggyBank },
    { key: "margin", label: "Marge", net: data ? `${data.margin_percent_net}%` : undefined, gross: data ? `${data.margin_percent_gross}% brutto` : undefined, Icon: Percent, isPercent: true },
  ], [data]);

  return (
    <div data-testid="revenue-stats-view">
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-3">Umsatzstatistik</h1>
      <p className="text-[#999] mb-2 max-w-2xl">
        Verkaufspreis minus Einkaufspreis je Reparatur – so siehst du genau, wie viel an jedem Auftrag verdient wird.
        Beide Preise werden als Brutto (inkl. MwSt.) eingegeben; hier siehst du zusätzlich den Netto-Wert.
      </p>
      {data && (
        <p data-testid="tax-mode-note" className="text-xs font-mono text-[#777] mb-8">
          {ku ? "Kleinunternehmer gemäß § 19 UStG – Netto = Brutto (keine MwSt. abziehbar)." : `Regelbesteuert – Netto errechnet mit ${data.tax_rate}% MwSt.`}
        </p>
      )}

      {/* Date range controls */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-1 bg-[#0a0a0a] border border-white/10 rounded-full p-1">
          {[6, 12].map((n) => (
            <button key={n} onClick={() => { setMode("preset"); setRange(n); }} data-testid={`profit-range-${n}`}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${mode === "preset" && range === n ? "bg-white text-[#050505]" : "text-[#aaa] hover:text-white"}`}>
              {n} Monate
            </button>
          ))}
          <button onClick={() => setMode("custom")} data-testid="profit-range-custom"
            className={`text-xs px-3 py-1.5 rounded-full transition-colors inline-flex items-center gap-1.5 ${mode === "custom" ? "bg-white text-[#050505]" : "text-[#aaa] hover:text-white"}`}>
            <CalendarRange size={13} /> Benutzerdefiniert
          </button>
        </div>
        {mode === "custom" && (
          <div className="flex items-center gap-2">
            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="profit-range-start"
              className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:border-white/40 focus:outline-none" />
            <span className="text-[#666] text-sm">bis</span>
            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="profit-range-end"
              className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:border-white/40 focus:outline-none" />
          </div>
        )}
        <button onClick={exportPdf} disabled={exporting || loading} data-testid="profit-export-pdf"
          className="ml-auto inline-flex items-center gap-2 rounded-full border border-white/15 text-[#ccc] px-4 py-2 text-sm hover:text-white hover:border-white/40 transition-colors disabled:opacity-40">
          <Download size={14} /> {exporting ? "Erstelle…" : "Als PDF exportieren"}
        </button>
      </div>
      <p data-testid="revenue-live-indicator" className="flex items-center gap-2 text-xs text-[#666] mb-6 -mt-2">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
        </span>
        Aktualisiert sich automatisch, sobald neuer Umsatz gebucht wird
      </p>

      {loading ? (
        <p className="text-[#888] font-mono text-sm py-20 text-center">Lädt…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {cards.map((c) => (
              <div key={c.key} data-testid={`profit-stat-${c.key}`} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-xs uppercase tracking-widest text-[#888]">{c.label}</p>
                  <c.Icon size={16} className="text-[#666]" />
                </div>
                <p className="font-display text-3xl lg:text-4xl mt-3">
                  {c.net === undefined ? "—" : c.isPercent ? c.net : `${Number(c.net).toFixed(2)} €`}
                </p>
                <p className="text-xs text-[#666] mt-1">
                  {c.isPercent ? c.gross : c.gross === undefined ? "" : `Brutto: ${Number(c.gross).toFixed(2)} €`}
                </p>
              </div>
            ))}
          </div>

          <div data-testid="profit-chart" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 mb-10">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="font-mono text-xs uppercase tracking-widest text-[#888]">
                Umsatz, Kosten &amp; Gewinn (Netto) · {data?.range?.start} bis {data?.range?.end}
              </p>
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={data?.monthly || []} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: "#888", fontSize: 11 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: "rgba(255,255,255,0.04)" }}
                    contentStyle={{ background: "#050505", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }}
                    formatter={(v, name) => [`${Number(v).toFixed(2)} €`, name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, color: "#999" }} />
                  <Bar dataKey="revenue_net" name="Umsatz (Netto)" fill="#ffffff" radius={[6, 6, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="cost_net" name="Kosten (Netto)" fill="#f87171" radius={[6, 6, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="profit_net" name="Gewinn (Netto)" fill="#34d399" radius={[6, 6, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="relative">
            <p className="sm:hidden text-xs text-[#666] mb-2">Tabelle nach rechts wischen für mehr Details</p>
            <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#0a0a0a]">
            <table className="w-full min-w-[820px]" data-testid="profit-orders-table">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#888] border-b border-white/10">
                  <th className="p-4">Referenz</th>
                  <th className="p-4">Gerät / Reparatur</th>
                  <th className="p-4">Verkauf (Netto / Brutto)</th>
                  <th className="p-4">Einkauf (Netto / Brutto)</th>
                  <th className="p-4">Gewinn (Netto / Brutto)</th>
                  <th className="p-4">Zahlung</th>
                </tr>
              </thead>
              <tbody>
                {(data?.orders || []).map((o) => (
                  <tr key={o.id} data-testid={`profit-order-${o.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-4 font-mono text-xs text-white/90">{o.reference || "—"}</td>
                    <td className="p-4">
                      <p className="text-sm text-white">{o.device}</p>
                      <p className="text-xs text-[#888] mt-0.5">{o.service_label}</p>
                    </td>
                    <td className="p-4 font-mono text-sm text-white">
                      {o.revenue_net.toFixed(2)} €
                      <span className="block text-xs text-[#666]">Brutto {o.revenue_gross.toFixed(2)} €</span>
                    </td>
                    <td className="p-4 font-mono text-sm text-[#999]">
                      {o.cost_net.toFixed(2)} €
                      <span className="block text-xs text-[#666]">Brutto {o.cost_gross.toFixed(2)} €</span>
                    </td>
                    <td className={`p-4 font-mono text-sm ${o.profit_net >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {o.profit_net.toFixed(2)} €
                      <span className="block text-xs text-[#666]">Brutto {o.profit_gross.toFixed(2)} €</span>
                    </td>
                    <td className="p-4 text-xs">
                      <span className={o.payment_status === "bezahlt" ? "text-emerald-400" : o.payment_status === "erstattet" ? "text-red-400" : "text-[#aaa]"}>
                        {PAYMENT_LABEL[o.payment_status] || o.payment_status}
                      </span>
                      <span className="block text-[#666] mt-0.5">{STATUS_LABEL[o.status] || o.status}</span>
                    </td>
                  </tr>
                ))}
                {(!data?.orders || data.orders.length === 0) && (
                  <tr><td colSpan={6} className="p-10 text-center text-[#888]">Keine Bestellungen in diesem Zeitraum.</td></tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
